import { FC, MouseEvent, useCallback, useEffect, useState } from 'react';
import { GenericDocumentDto, DocumentScopeEnum, GenericColors, ContractDetailsGlobal } from "api/contract";
import { contractsAPI } from "services/swagger-api";
import GenericBadge from 'components/ui/Badge';
import { GenericLoading } from 'components/ui/Loader';
import { Button } from 'components/ui/Button';
import { EyeIcon } from 'components/icons/Icons';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';
import { format } from 'date-fns';
import { SectionOutlet } from 'components/layout/Section/SectionOutlet';
import { scopeLabels } from 'shared/constants/scopeLabels';
import { FileCheck, FilePenLine, FileText, FolderOpen, Home, LucideIcon, Package, Receipt, Settings, UserCheck, Users } from 'lucide-react';
import { CardTableColumn } from 'components/display/Card/CardTable/types';
import { CardTableCollapsible } from 'components/display/Card/CardTable/CardTableCollapsible';
import { useDocumentViewerDialog } from 'components/domain/Document/useDocumentViewerDialog';

interface ContractDocumentsProps {
  contractId: string;
  riskId: string;
  contractDetails: ContractDetailsGlobal;
}

interface DocumentsByScope {
  scope: DocumentScopeEnum;
  title: string;
  icon: LucideIcon
  documents: GenericDocumentDto[];
}

const scopeOrder: Record<string, number> = {
  [DocumentScopeEnum.Contract]: 1,
  [DocumentScopeEnum.Quote]: 2,
  [DocumentScopeEnum.Product]: 3,
  [DocumentScopeEnum.Amendment]: 4,
  [DocumentScopeEnum.Receipt]: 5,
  [DocumentScopeEnum.Broker]: 6,
  [DocumentScopeEnum.System]: 7,
  [DocumentScopeEnum.Tenant]: 8,
  [DocumentScopeEnum.Subscriber]: 9,
  [DocumentScopeEnum.BorrowerProject]: 10
};

export const scopeIcons: Record<string, LucideIcon> = {
  [DocumentScopeEnum.Quote]: FileText,
  [DocumentScopeEnum.Contract]: FileCheck,
  [DocumentScopeEnum.Product]: Package,
  [DocumentScopeEnum.Amendment]: FilePenLine,
  [DocumentScopeEnum.Receipt]: Receipt,
  [DocumentScopeEnum.Broker]: Users,
  [DocumentScopeEnum.System]: Settings,
  [DocumentScopeEnum.Tenant]: Home,
  [DocumentScopeEnum.Subscriber]: UserCheck,
  [DocumentScopeEnum.BorrowerProject]: FolderOpen,
};

export const ContractDocuments: FC<ContractDocumentsProps> = ({ contractId, riskId, contractDetails }) => {
  const { t } = useDynamicTranslation([NsKey.Contract, NsKey.Common, NsKey.Document]);
  const { openDocumentViewer } = useDocumentViewerDialog();
  const [documentsByScope, setDocumentsByScope] = useState<DocumentsByScope[]>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setIsLoading(true);
        const response = await contractsAPI.getDocuments({ contractId });
        if (response) {
          const items = response.documents.reduce((acc, current) => {
            const index = acc.findIndex(item => item.scope === current.scope);
            if (acc[index]) {
              acc[index].documents.push(current);
            } else {
              acc.push({
                scope: current.scope,
                title: scopeLabels[current.scope],
                icon: scopeIcons[current.scope],
                documents: [current]
              });
            }
            return acc;
          }, [] as DocumentsByScope[]);
          items.sort((a, b) => scopeOrder[a.scope] - scopeOrder[b.scope]);
          setDocumentsByScope(items);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des documents:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDocuments();
  }, [contractId]);

  const handleDocumentClick = (document: GenericDocumentDto) => {
    if (!document.storedFileId) {
      return;
    }

    const sendTo = [{
      email: contractDetails.insured.email,
      name: contractDetails.insured.label,
    }]

    openDocumentViewer({
      title: t(document.label) || 'Document',
      storedFileId: document.storedFileId,
      emailConfig: {
        storedFileId: document.storedFileId,
        sendTo,
        risqueAssureId: riskId,
        fileTypeInt: document.fileType,
        fileName: `${t(document.label)}.pdf`
      }
    });
  };

  const renderStatus = useCallback((label?: string, color?: GenericColors) => {
    if (label) {
      return <GenericBadge
        label={label}
        color={color ? color : GenericColors.Gray}
        badgeType="sm-bold"
      />
    } else {
      return '_';
    }
  }, []);

  // Configuration des colonnes
  const columns: CardTableColumn<GenericDocumentDto>[] = [
    {
      key: 'document',
      label: 'Document',
      width: 'col-span-5',
      render: (document) => (
        <div className="flex items-start justify-center flex-col">
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDocumentClick(document);
              }}
              className="text-primary font-semibold hover:text-primary hover:underline transition-colors"
              disabled={!document.storedFileId}
            >
              {t(document.label)}
            </button>
            <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
              v{document.version}
            </span>
          </div>
          {document.signatureDate && (
            <p className='text-xs text-gray-400 mt-1'>
              {`${t('COMMON.LABELS.SIGNED_AT')} ${format(new Date(document.signatureDate), "dd MMM yyyy HH:mm")}`}
            </p>
          )}
        </div>
      )
    },
    {
      key: 'creationDate',
      label: 'Date création',
      width: 'col-span-3',
      render: (document) => (
        <div className="text-gray-600 text-sm">
          {formatDateToFrench(document.creationDate)}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Statut',
      width: 'col-span-3',
      render: (document) => renderStatus(document.statusLabel, document.statusColor)
    }
  ];

  if (!documentsByScope || isLoading) {
    return (
      <div className="relative flex justify-center items-center p-8">
        <GenericLoading isParentSized />
      </div>
    );
  }

  return (
    <SectionOutlet title={t('CONTRACT.LABELS.CONTRACT_DOCUMENTS')}>
      <div className='space-y-6'>
        {documentsByScope.map((scope, scopeIndex) => (
          <CardTableCollapsible
            key={`scope-${scope.scope}-${scopeIndex}`}
            title={t(scope.title)}
            icon={scope.icon}
            defaultExpanded={scope.scope === DocumentScopeEnum.Contract}
            titleAside={
              <span className="text-xs font-medium text-gray-500 bg-white px-3 py-1 rounded-full">
                {scope.documents.length} document{scope.documents.length > 1 ? 's' : ''}
              </span>
            }
            columns={columns}
            data={scope.documents}
            actions={(document) => (
              <Button
                round
                color='slate'
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  handleDocumentClick(document);
                }}
                disabled={!document.storedFileId}
              >
                <EyeIcon className="w-5 h-5" />
              </Button>
            )}
            emptyStateMessage="Aucun document trouvé"
          />
        ))}
      </div>

      {/* Empty State global */}
      {documentsByScope.length === 0 && (
        <div className="px-6 py-12 text-center">
          <div className="text-gray-500 text-sm">
            Aucun document trouvé
          </div>
        </div>
      )}
    </SectionOutlet>
  );
};

/**
 * Formate une date en format français (DD/MM/YYYY)
 */
function formatDateToFrench(date: Date): string {
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}
